import { contains, equals, find, not, propEq } from "ramda";
import { BC_HQ, BC_REPORTS, WORKFORCE } from "app_constants";
import { findAppFromStoreByAppName } from "helpers/brand";
import type { IBrandGroup } from "helpers/transforms/entities";
import type { IEntity, IEntityApp } from "interfaces/app";
import type {
  IFeatureMeta,
  ISecondaryNavProps,
  ISectionProps,
} from "types/navigation";

const findSection = (
  name: string,
  navItems: readonly ISectionProps[]
): ISectionProps | undefined => find(propEq("path", `/${name}`), navItems);

const findFeature = (
  path: string,
  sectionRoutes: ISecondaryNavProps[]
): ISecondaryNavProps => find(propEq("path", path))(sectionRoutes);

/**
 * @deprecated Probably `useCurrentNavItem` fits your use
 * @param navItems
 * @param section
 * @param feature
 * @param tab
 */
const findSecondaryNavItem = (
  navItems: readonly ISectionProps[],
  section: string,
  feature: string | undefined,
  tab: string | undefined
): IFeatureMeta | undefined => {
  const activeSection = findSection(section, navItems);
  if (!activeSection) {
    return undefined;
  }
  if (feature) {
    const featurePath = `/${section}/${feature}`;
    const activeFeature = findFeature(featurePath, activeSection.subSections);
    if (activeFeature && activeFeature.tabNavItems && tab) {
      const tabPath = featurePath + `/${tab}`;
      return findFeature(tabPath, activeFeature.tabNavItems);
    }
    return activeFeature;
  } else {
    return activeSection;
  }
};

const isBreadcrumbFeature = (featureMeta: IFeatureMeta): boolean => {
  return equals(featureMeta.app, BC_REPORTS) || equals(featureMeta.app, BC_HQ);
};

function isWorkforceFeature(featureMeta: IFeatureMeta) {
  return equals(featureMeta.app, WORKFORCE);
}

function getAppSpecificStoreKey(
  featureMeta: IFeatureMeta,
  currentLocationMeta: IEntity
): string | null {
  let app: IEntityApp | undefined;
  if (isBreadcrumbFeature(featureMeta)) {
    app = findAppFromStoreByAppName("breadcrumb", currentLocationMeta);
  } else if (isWorkforceFeature(featureMeta)) {
    app = findAppFromStoreByAppName(
      "workforce_scheduling",
      currentLocationMeta
    );
  } else {
    app = findAppFromStoreByAppName("swipely", currentLocationMeta);
  }
  if (app) {
    return app.storeKey;
  } else {
    return null;
  }
}

const validateOrCorrectLocationLevel = (
  vendorLevel: string,
  vendorLocation: string,
  featureMeta: IFeatureMeta,
  brandGroupMeta?: IBrandGroup
) => {
  let validatedVendorLevel = vendorLevel;
  let validatedVendorLocation = vendorLocation;
  if (brandGroupMeta) {
    if (equals(vendorLevel, "s") && not(contains("store", featureMeta.level))) {
      // No store level for this feature, correct level to brand
      validatedVendorLevel = "b";
      validatedVendorLocation = brandGroupMeta
        ? brandGroupMeta.brandId
        : vendorLocation;
    } else if (
      equals(vendorLevel, "b") &&
      not(contains("brand", featureMeta.level))
    ) {
      // No brand level for this feature, correct level to store
      validatedVendorLevel = "s";
      validatedVendorLocation = brandGroupMeta
        ? brandGroupMeta.stores[0].id
        : vendorLocation;
    }
  }
  // default brand menu components to brand level
  if (contains("/brand-menus/", featureMeta.path)) {
    validatedVendorLevel = "b";
    validatedVendorLocation = brandGroupMeta
      ? brandGroupMeta.brandId
      : vendorLocation;
  }

  return {
    vendorLevel: validatedVendorLevel,
    vendorLocation: validatedVendorLocation,
  };
};

export {
  findSecondaryNavItem,
  findSection,
  getAppSpecificStoreKey,
  isBreadcrumbFeature,
  isWorkforceFeature,
  validateOrCorrectLocationLevel,
};
